import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Asset, AssetService} from '@app/asset';
import {GroupService} from '@app/group/group.service';
import {AppState, StateService} from '@app/state/state.service';
import {UploadDialogComponent} from '@app/upload/upload-dialog/upload-dialog.component';
import {from, Observable, of, Subject, tap} from 'rxjs';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {catchError, map, mergeMap, reduce} from 'rxjs/operators';

export class UploadItem {
    file: File;
    progress: Observable<number>;
    message: string;
    assetId: string;
    name: string;
    extension: string;
    description: string;

    constructor(file: File) {
        this.file = file;
        this.name = file.name.slice(0, file.name.lastIndexOf('.'));
        this.extension = file.name.split('.').pop();
    }

    get fileName() {
        return this.name + '.' + this.extension;
    }
}

export class UploadData {
    uploadItems: UploadItem[];
    asGroup: boolean;
    streamId: string;
    tags: string[];
    parentAssetId: string;

    constructor(streamId: string) {
        this.streamId = streamId;
        this.asGroup = false;
        this.uploadItems = [];
        this.tags = [];
        this.parentAssetId = null;
    }

    public isValid(): boolean {
        if (!Boolean(this.streamId) && !this.parentAssetId) {
            return false;
        }

        if (this.uploadItems.length === 0) {
            return false;
        }

        return true;
    }
}

export class UploadProgress {
    uploadItem: UploadItem;
    index: number;
    total: number;
    preview: string | ArrayBuffer;
}

@Injectable({
    providedIn: 'root',
})
export class UploadService {
    public progress$: Subject<UploadProgress>;
    public assetUploaded$: Observable<Asset>;

    private state: AppState;
    private _assetUploadedSubject: Subject<Asset>;

    constructor(
        private assetService: AssetService,
        private groupService: GroupService,
        private stateService: StateService,
        private matSnackBar: MatSnackBar,
        private http: HttpClient,
    ) {
        this.progress$ = new Subject<UploadProgress>();
        this.stateService.state.subscribe((state) => (this.state = state));

        this._assetUploadedSubject = new Subject<Asset>();
        this.assetUploaded$ = this._assetUploadedSubject.asObservable();
    }

    upload(uploadData: UploadData): Observable<Asset[]> {
        let counter = 0;
        const total = uploadData.uploadItems.length;

        return from(uploadData.uploadItems).pipe(
            mergeMap((item) => {
                counter++;
                const progress = new UploadProgress();
                progress.uploadItem = item;
                progress.index = counter;
                progress.total = total;
                this.readImage(item)
                    .pipe(
                        map((preview) => {
                            if (typeof preview === 'string' && preview.includes('data:video/')) {
                                progress.preview = 'assets/pixel.png';
                            } else {
                                progress.preview = preview;
                            }

                            this.progress$.next(progress);
                        }),
                    )
                    .subscribe();

                return this.uploadItem(
                    item,
                    uploadData.streamId,
                    uploadData.tags,
                    uploadData.parentAssetId,
                );
            }, 1),
            reduce((acc: Asset[], value) => {
                let accumulator = [];
                if (Array.isArray(acc)) {
                    // subsequent passes
                    accumulator = acc;
                } else {
                    // First pass, acc is an object
                    accumulator.push(acc);
                }
                accumulator.push(value);

                return accumulator;
            }),
            mergeMap((assets) => {
                if (uploadData.asGroup && assets.length > 1 && this.state.viewingAsset == null) {
                    const assetIds = assets
                        .filter((asset) => asset != null)
                        .map((asset) => asset.id);
                    return this.groupService
                        .create(assetIds, uploadData.streamId)
                        .pipe(map((groupAsset) => [groupAsset]));
                }
                return of(assets);
            }),
            tap(() => {
                this.progress$.next(null);
                this.assetService.filter(true);
            }),
        );
    }

    getUploadData(
        dataTransferItems: DataTransferItemList | DataTransferItem[],
        streamId: string,
    ): UploadData {
        const uploadData = new UploadData(streamId || '');
        uploadData.uploadItems = Array.from(dataTransferItems)
            .map(item => item.getAsFile())
            .filter(file => file !== null)
            .map(file => new UploadItem(file)
            );

        return uploadData;
    }

    showUploadDialog() {
        this.matSnackBar.openFromComponent(UploadDialogComponent, {
            horizontalPosition: 'left',
        });
    }

    private readImage(uploadItem: UploadItem): Observable<string | ArrayBuffer> {
        const promise = new Promise<string | ArrayBuffer>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(uploadItem.file);
            reader.onload = (event) => {
                resolve(reader.result);
            };
            reader.onerror = () => {
                reject(null);
            };
        });

        return fromPromise(promise);
    }

    private errorNotification(message: string) {
        this.matSnackBar.open(message, 'Ok', {
            panelClass: 'error-snackbar',
            verticalPosition: 'top',
            horizontalPosition: 'center',
            duration: 0,
        });
    }

    private uploadItem(
        item: UploadItem,
        streamId: string,
        tags: string[],
        parentAssetId: string = null,
    ): Observable<object> {
        const formData = new FormData();
        formData.append('file', item.file, item.fileName);
        formData.append('streams', streamId || '');
        formData.append('name', item.name);
        if (item.assetId !== undefined) {
            formData.append('asset_id', item.assetId);
        }
        if (Boolean(item.description)) {
            formData.append('description', item.description);
        }
        if (tags.length > 0) {
            formData.append('tags', tags.join(','));
        }
        if (parentAssetId !== null) {
            formData.append('parent_asset_id', parentAssetId);
        }

        return this.http.post<Asset>('/api/upload/', formData).pipe(
            tap((asset) => {
                this._assetUploadedSubject.next(asset);
            }),
            catchError((err) => {
                if (err.status === 413) {
                    this.errorNotification('File too large to upload');
                }

                if (err.status === 500) {
                    this.errorNotification(
                        'There was an error on the server, please contact support',
                    );
                    this.matSnackBar.open(
                        'There was an error on the server, please contact support',
                        'Ok',
                        {
                            panelClass: 'error-snackbar',
                            verticalPosition: 'top',
                            horizontalPosition: 'center',
                            duration: 0,
                        },
                    );
                }

                if (err.error?.error) {
                    this.errorNotification(err.error.error);
                }

                if (err.error?.detail) {
                    this.errorNotification(err.error.detail);
                }

                return of(null);
            }),
        );
    }
}
